import { ApplicationConfig, inject, LOCALE_ID, provideAppInitializer, provideZonelessChangeDetection } from '@angular/core';
import { PreloadingStrategy, provideRouter, Route, withPreloading } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { DomSanitizer } from '@angular/platform-browser';
import { MatIconRegistry } from '@angular/material/icon';
import { routes } from './app.routes';
import { AuthenticationService } from '@app/core/auth';
import { HTTP_INTERCEPTORS_CHAIN } from '@core/api/interceptors';
import { provideTranslateService } from "@ngx-translate/core";
import { provideTranslateHttpLoader } from "@ngx-translate/http-loader";
import { CookieService } from 'ngx-cookie-service';
import { SessionManager } from '@core/session/session-manager.service';
import { EnvironmentService } from '@core/services/environment.service';
import { NoopSessionManagerService } from '@core/session/noop-session-manager.service';
import { CookieSessionManagerService } from '@core/session/session-cookie-manager.service';
import { SpanishMatPaginatorIntl } from '@shared/utils/spanish-paginator-intl';
import { DDMMYY_FORMATS } from '@shared/utils/date-formats';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { Observable, of } from 'rxjs';

function initializeMatIcons(): void {
  const iconRegistry = inject(MatIconRegistry);
  const sanitizer = inject(DomSanitizer);
  const path = 'assets/icons/';
  iconRegistry
    .addSvgIcon('facebook',  sanitizer.bypassSecurityTrustResourceUrl(path + 'facebook.svg'))
    .addSvgIcon('instagram', sanitizer.bypassSecurityTrustResourceUrl(path + 'instagram.svg'))
    .addSvgIcon('tiktok',    sanitizer.bypassSecurityTrustResourceUrl(path + 'tiktok.svg'))
    .addSvgIcon('x',         sanitizer.bypassSecurityTrustResourceUrl(path + 'x.svg'))
    .addSvgIcon('linkedin',  sanitizer.bypassSecurityTrustResourceUrl(path + 'linkedin.svg'))
    .addSvgIcon('whatsapp',  sanitizer.bypassSecurityTrustResourceUrl(path + 'whatsapp.svg'))
    .addSvgIcon('youtube',   sanitizer.bypassSecurityTrustResourceUrl(path + 'youtube.svg'));
}

function initializeAuth() {
  return inject(AuthenticationService).init();
}

export class SelectivePreloading implements PreloadingStrategy {
  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    return route.data?.['preload'] ? load() : of(null);
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(routes, withPreloading(SelectivePreloading)),
    provideAnimationsAsync(),
    provideHttpClient(
      withFetch(),
      withInterceptors(HTTP_INTERCEPTORS_CHAIN)
    ),
    provideNativeDateAdapter(),
    provideAppInitializer(initializeAuth),
    provideAppInitializer(initializeMatIcons),
    provideTranslateService({
      defaultLanguage: 'es',
      loader: provideTranslateHttpLoader({
        prefix: './assets/i18n/',
        suffix: '.json'
      })
    }),
    CookieService,
    SelectivePreloading,
    { provide: MatPaginatorIntl, useClass: SpanishMatPaginatorIntl },
    { provide: LOCALE_ID, useValue: 'es-MX' },
    { provide: MAT_DATE_LOCALE, useValue: 'es-MX' },
    { provide: MAT_DATE_FORMATS, useValue: DDMMYY_FORMATS },
    {
      provide: SessionManager,
      useFactory: () => {
        const envService = inject(EnvironmentService);
        if (envService.isProduction) {
          return new NoopSessionManagerService();
        }
        return new CookieSessionManagerService();
      },
    }
  ],
};
